import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Folder as FolderIcon,
  FolderShared as FolderSharedIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  useFolders,
  useSearchAllFolders,
} from "src/api/projectFolders/projectFolderService";
import { isSharedFolder } from "src/api/projectFolders/types";
import type { ProjectFolder } from "src/api/projectFolders/types";
import { useDebounce } from "src/reusable/hooks/useDebounce";
import {
  TreeNavProvider,
  useTreeKeyboardNav,
  useTreeNavItem,
} from "./hooks/useTreeKeyboardNav";
import ListSkeleton from "./components/ListSkeleton";
import RetryableError from "./components/RetryableError";
import { treeIndent } from "./helpers/treeIndent";
import "src/features/library/dialogs/library-dialog.scss";

// Writable-destination picker for file and folder moves (spec §5.8: a move
// needs write on both ends, so only write/owner rows are selectable).
// Deliberately store-free: expansion and selection are dialog-local, unlike
// FolderTreeItem whose state persists in projectsLayoutStore.
interface MoveDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  // Subtree to hide entirely. A folder cannot move into itself or its own
  // descendants, so the moved folder's subtree is never offered.
  pruneFolderId?: number;
  // Why this row cannot be chosen (shown as a tooltip), or null if it can.
  disabledReason: (folder: ProjectFolder) => string | null;
  onMove: (destination: ProjectFolder) => void;
  isMoving: boolean;
  error: string | null;
  // Confirm-button text; the picker also serves copy-style actions
  // (workspace promotion) where "Move" would be wrong.
  confirmLabel?: string;
}

const isWritable = (folder: ProjectFolder) =>
  folder.permission_level === "write" || folder.permission_level === "owner";

// Not selectable but still rendered: a read-only or traversal-only folder
// can hold a writable descendant the user needs to reach.
export const writabilityReason = (folder: ProjectFolder): string | null =>
  isWritable(folder) && !folder.is_traversal_only
    ? null
    : "You need edit access to move items here";

interface PickerRowProps {
  folder: ProjectFolder;
  level: number;
  pruneFolderId?: number;
  disabledReason: (folder: ProjectFolder) => string | null;
  selectedId: number | null;
  onSelect: (folder: ProjectFolder) => void;
}

const PickerRow: React.FC<PickerRowProps> = ({
  folder,
  level,
  pruneFolderId,
  disabledReason,
  selectedId,
  onSelect,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { data: children, isLoading } = useFolders(expanded ? folder.id : null);
  const mayHaveChildren = (folder.child_count ?? 1) > 0;
  const hasChildren = !!children && children.length > 0;
  const showExpander = expanded ? hasChildren : mayHaveChildren;

  const reason = disabledReason(folder);
  const selectable = reason === null;
  const isSelected = selectedId === folder.id;

  const handleClick = () => {
    if (selectable) {
      onSelect(folder);
    } else if (showExpander) {
      setExpanded((prev) => !prev);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    } else if (e.key === "ArrowRight" && !expanded) {
      e.preventDefault();
      setExpanded(true);
    } else if (e.key === "ArrowLeft" && expanded) {
      e.preventDefault();
      setExpanded(false);
    }
  };

  const Icon = isSharedFolder(folder) ? FolderSharedIcon : FolderIcon;

  const navItem = useTreeNavItem(folder.id);

  const row = (
    <Box
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="treeitem"
      {...navItem}
      aria-selected={isSelected || undefined}
      aria-expanded={hasChildren ? expanded : undefined}
      aria-disabled={selectable ? undefined : true}
      aria-label={folder.name}
      className={`library-dialog__row${isSelected ? " is-selected" : ""}${
        selectable ? "" : " is-disabled"
      }`}
      sx={{
        display: "flex",
        alignItems: "center",
        py: 0.5,
        px: 1,
        pl: treeIndent(level, 1),
        borderRadius: 1,
        cursor: selectable || showExpander ? "pointer" : "default",
      }}
      data-testid={`move-picker-folder-${folder.id}`}
    >
      <IconButton
        className="library-dialog__expander"
        size="small"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((prev) => !prev);
        }}
        aria-label={expanded ? "Collapse folder" : "Expand folder"}
        aria-expanded={expanded}
        sx={{
          p: 0.25,
          mr: 0.5,
          visibility: showExpander ? "visible" : "hidden",
        }}
        data-testid={`move-picker-expand-${folder.id}`}
      >
        {isLoading ? (
          <CircularProgress size={16} />
        ) : expanded ? (
          <ExpandMoreIcon fontSize="small" />
        ) : (
          <ChevronRightIcon fontSize="small" />
        )}
      </IconButton>
      <Icon
        className="library-dialog__row-icon"
        fontSize="small"
        sx={{ mr: 1 }}
      />
      <Typography
        variant="body2"
        className="library-dialog__row-label"
        sx={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {folder.name}
      </Typography>
    </Box>
  );

  return (
    <Box>
      {selectable ? (
        row
      ) : (
        <Tooltip title={reason} placement="right">
          {row}
        </Tooltip>
      )}
      {expanded && hasChildren && (
        <Box>
          {children
            .filter((child) => child.id !== pruneFolderId)
            .map((child) => (
              <PickerRow
                key={child.id}
                folder={child}
                level={level + 1}
                pruneFolderId={pruneFolderId}
                disabledReason={disabledReason}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            ))}
        </Box>
      )}
    </Box>
  );
};

const MoveDialog: React.FC<MoveDialogProps> = ({
  open,
  onClose,
  title,
  pruneFolderId,
  disabledReason,
  onMove,
  isMoving,
  error,
  confirmLabel = "Move",
}) => {
  const [selected, setSelected] = useState<ProjectFolder | null>(null);
  const {
    data: roots,
    isLoading: rootsLoading,
    isError: rootsError,
    refetch: refetchRoots,
  } = useFolders(undefined);

  const visibleRoots = (roots ?? []).filter((r) => r.id !== pruneFolderId);
  const { activeId, setActiveId, onKeyDown } = useTreeKeyboardNav(
    visibleRoots[0]?.id ?? null,
  );

  // A query jumps straight to a destination across the whole readable forest
  // (the tree is lazy, so deep folders aren't otherwise reachable without
  // clicking down). The hit carries its permission level, so the same
  // disabledReason gate applies. pruneFolderId is dropped here too; its
  // descendants fall back to the server move guard.
  const [search, setSearch] = useState("");
  const trimmedSearch = search.trim();
  const debouncedSearch = useDebounce(trimmedSearch, 300);
  const searching = trimmedSearch.length > 0;
  const { data: searchData, isFetching: isSearching } =
    useSearchAllFolders(debouncedSearch);
  const searchResults = (searchData?.items ?? []).filter(
    (h) => h.kind === "folder" && h.folder_id !== pruneFolderId,
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="library-dialog"
      data-testid="move-dialog"
      data-attr="folder-browser:modal:move"
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            className="library-dialog__title"
          >
            {title}
          </Typography>
          <IconButton
            className="library-dialog__close"
            onClick={onClose}
            size="small"
            aria-label="Close move dialog"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder="Search folders"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          data-testid="move-dialog-search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {searching ? (
          isSearching && searchResults.length === 0 ? (
            <ListSkeleton rows={4} height={28} testId="move-search-loading" />
          ) : searchResults.length === 0 ? (
            <Typography
              variant="body2"
              className="library-dialog__empty"
              sx={{ py: 2 }}
              data-testid="move-search-empty"
            >
              {`No folders match "${trimmedSearch}".`}
            </Typography>
          ) : (
            <Box role="listbox" aria-label="Destination folder search results">
              {searchResults.map((hit) => {
                const candidate: ProjectFolder = {
                  id: hit.folder_id,
                  name: hit.folder_name,
                  // owner_id is unused by the move flow; the picker needs only
                  // the id and the permission level the hit carries.
                  owner_id: 0,
                  permission_level: hit.permission_level,
                  is_traversal_only: false,
                };
                const reason = disabledReason(candidate);
                const selectable = reason === null;
                const isSelected = selected?.id === hit.folder_id;
                const parentPath = hit.relative_path
                  .split("/")
                  .slice(0, -1)
                  .join(" / ");
                const row = (
                  <Box
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={selectable ? undefined : true}
                    tabIndex={selectable ? 0 : -1}
                    onClick={() => selectable && setSelected(candidate)}
                    onKeyDown={(e) => {
                      if (selectable && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        setSelected(candidate);
                      }
                    }}
                    data-testid={`move-search-result-${hit.folder_id}`}
                    className={`library-dialog__row${
                      isSelected ? " is-selected" : ""
                    }${selectable ? "" : " is-disabled"}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      cursor: selectable ? "pointer" : "default",
                    }}
                  >
                    <FolderIcon
                      className="library-dialog__row-icon"
                      fontSize="small"
                      sx={{ flexShrink: 0 }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        className="library-dialog__row-label"
                        noWrap
                      >
                        {hit.folder_name}
                      </Typography>
                      {parentPath && (
                        <Typography
                          variant="caption"
                          className="library-dialog__row-sub"
                          noWrap
                          sx={{ display: "block" }}
                        >
                          {parentPath}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
                return selectable ? (
                  <Box key={hit.folder_id}>{row}</Box>
                ) : (
                  <Tooltip key={hit.folder_id} title={reason} placement="right">
                    {row}
                  </Tooltip>
                );
              })}
            </Box>
          )
        ) : rootsLoading ? (
          <ListSkeleton rows={4} height={28} testId="move-picker-loading" />
        ) : rootsError ? (
          <RetryableError
            message="Couldn't load destination folders."
            onRetry={() => void refetchRoots()}
            testId="move-picker-error"
          />
        ) : visibleRoots.length === 0 ? (
          <Typography
            variant="body2"
            className="library-dialog__empty"
            sx={{ py: 2 }}
          >
            No destination folders available.
          </Typography>
        ) : (
          <TreeNavProvider value={{ activeId, setActiveId }}>
            <Box
              role="tree"
              aria-label="Destination folder"
              onKeyDown={onKeyDown}
            >
              {visibleRoots.map((root) => (
                <PickerRow
                  key={root.id}
                  folder={root}
                  level={0}
                  pruneFolderId={pruneFolderId}
                  disabledReason={disabledReason}
                  selectedId={selected?.id ?? null}
                  onSelect={setSelected}
                />
              ))}
            </Box>
          </TreeNavProvider>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="move-dialog-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected || isMoving}
          onClick={() => selected && onMove(selected)}
          data-testid="move-dialog-confirm"
          data-attr="folder-browser:btn:confirm-move"
        >
          {isMoving ? <CircularProgress size={20} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveDialog;
